import React, { useEffect, useMemo, useState } from "react";
import { adminApi, toApiAbsoluteUrl } from "../../api/client";
import { useNavigate, Link } from "react-router-dom";
import { LinearProgress, Snackbar, Alert } from "@mui/material";
import { ChevronRight, FileText } from "lucide-react";

export default function Providers() {
  const [providers, setProviders] = useState([]);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const navigate = useNavigate();
  const [total, setTotal] = useState(0);
  const [searchText, setSearchText] = useState("");
  const [filters, setFilters] = useState({ q: "", page: 1, limit: 20 });
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "error",
  });

  // Edit modal state
  const [editModal, setEditModal] = useState({
    open: false,
    provider: null,
  });
  const [editForm, setEditForm] = useState({
    companyName: "",
    phoneNumber: "",
    businessType: "",
    address: "",
    description: "",
    numberOfListings: 0,
    packageAmount: 0,
    utrId: "",
    paymentStatus: "pending",
  });
  const [editLoading, setEditLoading] = useState(false);

  const normalizedQuery = useMemo(
    () => (filters.q || "").trim().toLowerCase(),
    [filters.q]
  );

  const toAbsoluteUrl = toApiAbsoluteUrl;

  function formatBytes(bytes) {
    if (bytes === 0) return "0 B";
    if (!bytes || Number.isNaN(bytes)) return "-";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB", "TB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    const value = bytes / Math.pow(k, i);
    return `${value.toFixed(value >= 100 ? 0 : value >= 10 ? 1 : 2)} ${
      sizes[i]
    }`;
  }

  function formatDate(iso) {
    if (!iso) return "-";
    try {
      const d = new Date(iso);
      return d.toLocaleString();
    } catch {
      return iso;
    }
  }

  useEffect(() => {
    const handle = setTimeout(() => {
      setFilters((p) => ({ ...p, q: searchText, page: 1 }));
    }, 200);
    return () => clearTimeout(handle);
  }, [searchText]);

  useEffect(() => {
    runSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.page, filters.limit]);

  async function runSearch(override = {}) {
    const merged = { ...filters, ...override };
    try {
      setLoading(true);
      const res = await adminApi.listProviders({
        page: Number(merged.page || 1),
        limit: Number(merged.limit || 20),
      });

      setProviders(res?.items || []);
      setTotal(res?.total || 0);
      setFilters((prev) => ({ ...prev, ...override }));
    } catch (_e) {
      setProviders([]);
      setTotal(0);
      const msg = _e?.data?.message || "Failed to load providers";
      setSnackbar({ open: true, message: msg, severity: "error" });
    } finally {
      setLoading(false);
    }
  }

  const filteredProviders = useMemo(() => {
    if (!normalizedQuery) return providers;
    return (providers || []).filter((p) => {
      const email = (p?.email || "").toLowerCase();
      const phone = (p?.phoneNumber || "").toLowerCase();
      const company = (p?.providerProfile?.companyName || "").toLowerCase();
      return (
        email.includes(normalizedQuery) ||
        phone.includes(normalizedQuery) ||
        company.includes(normalizedQuery)
      );
    });
  }, [providers, normalizedQuery]);

  // Edit modal functions
  const openEditModal = (provider) => {
    setEditModal({ open: true, provider });
    setEditForm({
      companyName: provider?.providerProfile?.companyName || "",
      phoneNumber: provider?.phoneNumber || "",
      businessType: provider?.providerProfile?.businessType || "",
      address: provider?.providerProfile?.address || "",
      description: provider?.providerProfile?.description || "",
      numberOfListings: provider?.providerProfile?.numberOfListings || 0,
      packageAmount: provider?.providerProfile?.packageAmount || 0,
      utrId: provider?.providerProfile?.utrId || provider?.utrId || "",
      paymentStatus:
        provider?.providerProfile?.paymentStatus ||
        provider?.paymentStatus ||
        "pending",
    });
  };

  const closeEditModal = () => {
    setEditModal({ open: false, provider: null });
    setEditForm({
      companyName: "",
      phoneNumber: "",
      businessType: "",
      address: "",
      description: "",
      numberOfListings: 0,
      packageAmount: 0,
      utrId: "",
      paymentStatus: "pending",
    });
  };

  const handleEditSubmit = async () => {
    if (!editModal.provider) return;

    setEditLoading(true);
    try {
      await adminApi.updateProvider(editModal.provider.id, editForm);

      setSnackbar({
        open: true,
        message: "Provider updated successfully",
        severity: "success",
      });

      closeEditModal();
      await runSearch();
    } catch (error) {
      console.error("Update provider error:", error);
      setSnackbar({
        open: true,
        message: error?.data?.message || "Failed to update provider",
        severity: "error",
      });
    } finally {
      setEditLoading(false);
    }
  };

  return (
    <div>
      <div className="mb-4 text-sm text-gray-600 flex items-center gap-1">
        <Link to="/providers" className="hover:text-gray-800">
          Dashboard
        </Link>
        <ChevronRight className="h-4 w-4 opacity-60" />
        <span className="text-gray-800 font-medium">Providers</span>
      </div>

      {/* Search bar */}
      <div className="bg-white shadow rounded p-4 mb-4 grid grid-cols-1 md:grid-cols-4 gap-3">
        <input
          type="text"
          placeholder="Search company, email or phone"
          className="border p-2 rounded"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        />
        <div className="flex items-center gap-2">
          <button
            className="px-4 py-2 bg-gray-600 text-white rounded"
            onClick={() => {
              setSearchText("");
              setFilters({ q: "", page: 1, limit: filters.limit });
              runSearch({ page: 1 });
            }}
          >
            Reset
          </button>
        </div>
      </div>

      {/* Responsive table */}
      <div className="overflow-x-auto">
        <table className="w-full text-center bg-white shadow rounded min-w-[800px]">
          <thead>
            <tr>
              <th className="p-2 border">Company</th>
              <th className="p-2 border">Email</th>
              <th className="p-2 border">Phone</th>
              <th className="p-2 border">Promoter</th>
              <th className="p-2 border">Package</th>
              <th className="p-2 border">Payment Status</th>
              <th className="p-2 border">Documents</th>
              <th className="p-2 border">Created</th>
              <th className="p-2 border">Updated</th>
              <th className="p-2 border">Action</th>
            </tr>
          </thead>
          <tbody>
            {filteredProviders.map((p) => {
              const company = p?.providerProfile?.companyName || "-";
              const docs = p?.providerProfile?.documents || [];
              const numberOfListings =
                p?.providerProfile?.numberOfListings || 0;
              const packageAmount = p?.providerProfile?.packageAmount || 0;
              const paymentStatus =
                p?.providerProfile?.paymentStatus ||
                p?.paymentStatus ||
                "pending";

              const getStatusColor = (status) => {
                switch (status) {
                  case "verified":
                    return "bg-green-100 text-green-800";
                  case "rejected":
                    return "bg-red-100 text-red-800";
                  default:
                    return "bg-yellow-100 text-yellow-800";
                }
              };

              return (
                <tr key={p.id}>
                  <td className="p-2 border">
                    {company !== "-" ? (
                      <button
                        className="text-blue-600 hover:text-blue-800 underline"
                        onClick={() =>
                          navigate(
                            `/dashboard/listings?providerUserId=${encodeURIComponent(
                              p.id
                            )}`
                          )
                        }
                        title="View listings"
                      >
                        {company}
                      </button>
                    ) : (
                      "-"
                    )}
                  </td>
                  <td className="p-2 border">{p.email}</td>
                  <td className="p-2 border">{p.phoneNumber || "-"}</td>
                  <td className="p-2 border">
                    {p?.promoterName ? (
                      <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800">
                        {p.promoterName}
                      </span>
                    ) : (
                      <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-600">
                        Independent
                      </span>
                    )}
                  </td>
                  <td className="p-2 border">
                    <div className="text-sm">
                      <div className="font-medium">
                        {numberOfListings} listings
                      </div>
                      {packageAmount > 0 && (
                        <div className="text-gray-600">
                          ₹{packageAmount.toLocaleString()}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="p-2 border">
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(
                        paymentStatus
                      )}`}
                    >
                      {paymentStatus}
                    </span>
                  </td>
                  <td className="p-2 border">{docs.length}</td>
                  <td className="p-2 border text-sm">
                    {formatDate(p?.providerProfile?.createdAt || p?.createdAt)}
                  </td>
                  <td className="p-2 border text-sm">
                    {formatDate(p?.providerProfile?.updatedAt || p?.updatedAt)}
                  </td>
                  <td className="p-2 border">
                    <div className="flex gap-2">
                      <button
                        onClick={() => setSelectedRecord(p)}
                        className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors text-sm"
                      >
                        View
                      </button>
                      <button
                        onClick={() => openEditModal(p)}
                        className="px-3 py-1 bg-green-500 text-white rounded hover:bg-green-600 transition-colors text-sm"
                      >
                        Edit
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {filteredProviders.length === 0 && (
              <tr>
                <td className="p-6 text-gray-500 text-center" colSpan={10}>
                  No providers
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between p-3 flex-wrap gap-2">
        <div className="text-sm text-gray-600">Total: {total}</div>
        <div className="flex items-center gap-2">
          <button
            className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50"
            disabled={filters.page <= 1}
            onClick={() =>
              runSearch({ page: Math.max(1, (filters.page || 1) - 1) })
            }
          >
            Prev
          </button>
          <span className="text-sm">Page {filters.page}</span>
          <button
            className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50"
            disabled={providers.length < filters.limit}
            onClick={() => runSearch({ page: (filters.page || 1) + 1 })}
          >
            Next
          </button>
          <select
            className="ml-2 border p-1 rounded text-sm"
            value={filters.limit}
            onChange={(e) =>
              runSearch({ limit: Number(e.target.value), page: 1 })
            }
          >
            <option value={10}>10 / page</option>
            <option value={20}>20 / page</option>
            <option value={50}>50 / page</option>
            <option value={100}>100 / page</option>
          </select>
        </div>
      </div>

      {/* Modal */}
      {selectedRecord && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded shadow-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold mb-4">User Details</h3>
            <p>Email: {selectedRecord.email}</p>
            <p>Role: {selectedRecord.role}</p>
            <p>Phone: {selectedRecord.phoneNumber || "-"}</p>
            {selectedRecord.providerProfile && (
              <>
                <p className="mt-2 font-semibold">Provider Profile</p>
                <p>Company: {selectedRecord.providerProfile.companyName}</p>
                <p>Phone: {selectedRecord.providerProfile.phoneNumber}</p>
                <p>
                  Address:{" "}
                  {selectedRecord.providerProfile.address || "Not provided"}
                </p>
                <p>
                  Company Type:{" "}
                  {selectedRecord.providerProfile.companyType ||
                    "Not specified"}
                </p>

                <div className="mt-3 p-3 bg-gray-50 rounded">
                  <p className="font-semibold text-gray-800">Listing Package</p>
                  <p>
                    Number of Listings:{" "}
                    {selectedRecord.providerProfile.numberOfListings || 0}
                  </p>
                  <p>
                    Package Amount:{" "}
                    {selectedRecord.providerProfile.packageAmount
                      ? `₹${selectedRecord.providerProfile.packageAmount.toLocaleString()}`
                      : "Not specified"}
                  </p>
                  <p>
                    UTR ID:{" "}
                    {selectedRecord.providerProfile.utrId ||
                      selectedRecord.utrId ||
                      "Not provided"}
                  </p>
                  <p>
                    Payment Status:
                    <span
                      className={`ml-2 px-2 py-1 rounded-full text-xs font-medium ${
                        selectedRecord.providerProfile.paymentStatus ===
                        "verified"
                          ? "bg-green-100 text-green-800"
                          : selectedRecord.providerProfile.paymentStatus ===
                            "rejected"
                          ? "bg-red-100 text-red-800"
                          : "bg-yellow-100 text-yellow-800"
                      }`}
                    >
                      {selectedRecord.providerProfile.paymentStatus ||
                        selectedRecord.paymentStatus ||
                        "pending"}
                    </span>
                  </p>
                </div>

                <p className="mt-2">
                  Documents:{" "}
                  {(selectedRecord.providerProfile.documents || []).length}
                </p>
                <div className="mt-3 max-h-72 overflow-y-auto border rounded">
                  {(selectedRecord.providerProfile.documents || []).length ===
                  0 ? (
                    <div className="p-4 text-sm text-gray-500">
                      No documents uploaded
                    </div>
                  ) : (
                    <table className="w-full text-left text-sm min-w-[600px]">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="p-2 border">Preview</th>
                          <th className="p-2 border">Name</th>
                          <th className="p-2 border">Type</th>
                          <th className="p-2 border">Size</th>
                          <th className="p-2 border">Uploaded</th>
                          <th className="p-2 border">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(selectedRecord.providerProfile.documents || []).map(
                          (d) => {
                            const isImage = (d?.mimeType || "").startsWith(
                              "image/"
                            );
                            return (
                              <tr key={d.id}>
                                <td className="p-2 border align-middle">
                                  {isImage ? (
                                    <button
                                      className="inline-flex items-center"
                                      onClick={() =>
                                        window.open(
                                          toAbsoluteUrl(d.url),
                                          "_blank",
                                          "noopener"
                                        )
                                      }
                                      title="Open image"
                                    >
                                      <img
                                        src={toAbsoluteUrl(d.url)}
                                        alt={
                                          d.originalName || d.kind || "document"
                                        }
                                        className="h-12 w-12 object-cover rounded"
                                      />
                                    </button>
                                  ) : (
                                    <div className="flex items-center text-gray-600">
                                      <FileText size={18} className="mr-1" />
                                      <span>File</span>
                                    </div>
                                  )}
                                </td>
                                <td className="p-2 border align-middle">
                                  {d.originalName || d.kind || "-"}
                                </td>
                                <td className="p-2 border align-middle">
                                  {d.mimeType || "-"}
                                </td>
                                <td className="p-2 border align-middle">
                                  {formatBytes(d.size)}
                                </td>
                                <td className="p-2 border align-middle">
                                  {formatDate(d.uploadedAt)}
                                </td>
                                <td className="p-2 border align-middle">
                                  <div className="flex items-center gap-2">
                                    <button
                                      className="px-2 py-1 text-blue-600 hover:text-blue-800 underline"
                                      onClick={() =>
                                        window.open(
                                          toAbsoluteUrl(d.url),
                                          "_blank",
                                          "noopener"
                                        )
                                      }
                                    >
                                      Open
                                    </button>
                                    <a
                                      className="px-2 py-1 text-gray-700 hover:text-gray-900 underline"
                                      href={toAbsoluteUrl(d.url)}
                                      download={d.originalName || true}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                    >
                                      Download
                                    </a>
                                  </div>
                                </td>
                              </tr>
                            );
                          }
                        )}
                      </tbody>
                    </table>
                  )}
                </div>
              </>
            )}
            <button
              onClick={() => setSelectedRecord(null)}
              className="mt-4 px-4 py-2 bg-red-500 text-white rounded"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Loader */}
      {loading && (
        <div className="fixed bottom-0 left-0 right-0 z-50">
          <LinearProgress />
        </div>
      )}

      {/* Edit Modal */}
      {editModal.open && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded shadow-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold">Edit Provider</h3>
              <button
                onClick={closeEditModal}
                className="text-gray-500 hover:text-gray-700"
              >
                ×
              </button>
            </div>

            <div className="space-y-4">
              {/* Basic Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Company Name *
                  </label>
                  <input
                    type="text"
                    value={editForm.companyName}
                    onChange={(e) =>
                      setEditForm({ ...editForm, companyName: e.target.value })
                    }
                    className="w-full border p-2 rounded-md"
                    disabled={editLoading}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone Number
                  </label>
                  <input
                    type="text"
                    value={editForm.phoneNumber}
                    onChange={(e) =>
                      setEditForm({ ...editForm, phoneNumber: e.target.value })
                    }
                    className="w-full border p-2 rounded-md"
                    disabled={editLoading}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Business Type
                  </label>
                  <input
                    type="text"
                    value={editForm.businessType}
                    onChange={(e) =>
                      setEditForm({ ...editForm, businessType: e.target.value })
                    }
                    className="w-full border p-2 rounded-md"
                    disabled={editLoading}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Address
                  </label>
                  <input
                    type="text"
                    value={editForm.address}
                    onChange={(e) =>
                      setEditForm({ ...editForm, address: e.target.value })
                    }
                    className="w-full border p-2 rounded-md"
                    disabled={editLoading}
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={editForm.description}
                    onChange={(e) =>
                      setEditForm({ ...editForm, description: e.target.value })
                    }
                    className="w-full border p-2 rounded-md"
                    rows={3}
                    disabled={editLoading}
                  />
                </div>
              </div>

              {/* Listing Package Section */}
              <div className="border-t pt-4">
                <h4 className="text-lg font-semibold text-gray-800 mb-4">
                  Listing Package
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Number of Listings
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="25"
                      value={editForm.numberOfListings}
                      onChange={(e) =>
                        setEditForm({
                          ...editForm,
                          numberOfListings: parseInt(e.target.value) || 0,
                        })
                      }
                      className="w-full border p-2 rounded-md"
                      disabled={editLoading}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Package Amount (₹)
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={editForm.packageAmount}
                      onChange={(e) =>
                        setEditForm({
                          ...editForm,
                          packageAmount: parseFloat(e.target.value) || 0,
                        })
                      }
                      className="w-full border p-2 rounded-md"
                      disabled={editLoading}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      UTR/Transaction ID
                    </label>
                    <input
                      type="text"
                      value={editForm.utrId}
                      onChange={(e) =>
                        setEditForm({ ...editForm, utrId: e.target.value })
                      }
                      className="w-full border p-2 rounded-md"
                      disabled={editLoading}
                      placeholder="Enter UTR or Transaction ID"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Payment Status
                    </label>
                    <select
                      value={editForm.paymentStatus}
                      onChange={(e) =>
                        setEditForm({
                          ...editForm,
                          paymentStatus: e.target.value,
                        })
                      }
                      className="w-full border p-2 rounded-md"
                      disabled={editLoading}
                    >
                      <option value="pending">Pending</option>
                      <option value="verified">Verified</option>
                      <option value="rejected">Rejected</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="flex gap-3 justify-end pt-4 border-t">
                <button
                  onClick={closeEditModal}
                  className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 transition-colors"
                  disabled={editLoading}
                >
                  Cancel
                </button>
                <button
                  onClick={handleEditSubmit}
                  className={`px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition-colors ${
                    editLoading ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                  disabled={editLoading}
                >
                  {editLoading ? "Updating..." : "Update Provider"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ open: false, message: "" })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() =>
            setSnackbar({ open: false, message: "", severity: "error" })
          }
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
}
